import { useEffect, useRef, useState } from 'react';
import { PATIENT_TRACKING_API_BASE } from '../patientTrackingTypes';
import { fetchPatientTrackingMentionCandidates } from './patientTrackingDiscussionApi';
import type { PatientTrackingDiscussionTargetType } from './patientTrackingDiscussionTypes';

export interface PtMentionCandidatePickerProps {
  targetType: PatientTrackingDiscussionTargetType;
  targetId: string;
  query: string;
  open: boolean;
  apiBase?: string;
  onSelect: (mentionToken: string) => void;
}

export function PtMentionCandidatePicker({
  targetType,
  targetId,
  query,
  open,
  apiBase = PATIENT_TRACKING_API_BASE,
  onSelect
}: PtMentionCandidatePickerProps) {
  const [items, setItems] = useState<
    { displayName: string; notifyEmail: string; mentionToken: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!open || !targetId) return;
    const id = ++requestId.current;
    setLoading(true);
    const handle = window.setTimeout(() => {
      void fetchPatientTrackingMentionCandidates(targetType, targetId, query, apiBase)
        .then((res) => {
          if (requestId.current !== id) return;
          setItems(
            (res.items ?? []).map((row) => ({
              displayName: row.displayName,
              notifyEmail: row.notifyEmail,
              mentionToken: row.mentionToken
            }))
          );
          setReason(res.reason ?? null);
        })
        .catch(() => {
          if (requestId.current !== id) return;
          setItems([]);
          setReason('mentions_unavailable');
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [targetType, targetId, open, query]);

  if (!open) return null;

  const emptyMessage =
    reason === 'mentions_disabled'
      ? 'Mentions are disabled.'
      : loading
        ? 'Loading…'
        : 'No matches. Pick from the list only—typing an email is not allowed.';

  return (
    <ul className="file-review-mention-picker" role="listbox" aria-label="Mention suggestions">
      {items.length === 0 ? (
        <li className="file-review-mention-picker__empty" role="option" aria-disabled>
          {emptyMessage}
        </li>
      ) : (
        items.map((item) => (
          <li key={item.notifyEmail} role="option">
            <button
              type="button"
              className="file-review-mention-picker__option"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(item.mentionToken)}
            >
              <span className="file-review-mention-picker__name">{item.displayName}</span>
              <span className="file-review-mention-picker__email">{item.notifyEmail}</span>
            </button>
          </li>
        ))
      )}
    </ul>
  );
}
